import React from 'react';
import PropTypes from 'prop-types';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';

/**
 * AgSUS Tabs View.
 *
 * Suporta 3 formatos:
 *
 * 1) items[] novo (com sub-blocks editáveis no corpo):
 *    items = [{ id, tabTitle, blocks, blocks_layout }]
 *
 * 2) items[] simples (content textarea — schema intermediário):
 *    items = [{ tabTitle, content }]
 *
 * 3) data.{blocks, blocks_layout} (legado tabs_block):
 *    data = { blocks: { <tabId>: { title, blocks, blocks_layout } },
 *             blocks_layout: { items: [<tabId>, ...] } }
 */
const TabsView = ({ data, metadata, properties }) => {
  const itemsArr = Array.isArray(data?.items) ? data.items : [];
  const newTabs = itemsArr.map((it, i) => ({
    id: it.id || `item-${i}`,
    title: it.tabTitle,
    blocks: it.blocks,
    blocks_layout: it.blocks_layout,
    content: it.content,
  }));

  const nested = data?.data;
  const nestedTabs = nested?.blocks_layout?.items?.length
    ? nested.blocks_layout.items.map((tid) => ({
        id: tid,
        title: nested.blocks[tid]?.title,
        blocks: nested.blocks[tid]?.blocks,
        blocks_layout: nested.blocks[tid]?.blocks_layout,
      }))
    : [];

  const tabs = newTabs.length > 0 ? newTabs : nestedTabs;
  const [idx, setIdx] = React.useState(0);
  const orientation = data?.orientation === 'vertical' ? 'vertical' : 'horizontal';
  const rootCls = `ag-tabs ag-tabs--${orientation}`;

  if (tabs.length === 0) {
    return (
      <section className={rootCls}>
        {data.title && <h2 className="ag-tabs__heading">{data.title}</h2>}
        <p className="ag-tabs__empty">Adicione abas na barra lateral.</p>
      </section>
    );
  }

  const active = tabs[idx] || tabs[0];

  return (
    <section className={rootCls}>
      {data.eyebrow && (
        <span className="ag-tabs__eyebrow">{data.eyebrow}</span>
      )}
      {data.title && <h2 className="ag-tabs__heading">{data.title}</h2>}

      <div className="ag-tabs__layout">
        <div
          className="tabs ag-tabs__nav"
          role="tablist"
          aria-orientation={orientation}
        >
          {tabs.map((t, i) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={i === idx}
              className={`tab ag-tabs__trigger${i === idx ? ' is-active' : ''}`}
              onClick={() => setIdx(i)}
            >
              {t.title || `Aba ${i + 1}`}
            </button>
          ))}
        </div>

        <div className="ag-tabs__panel" role="tabpanel">
          {orientation === 'vertical' && active?.title && (
            <h3 className="ag-tabs__paneltitle">{active.title}</h3>
          )}
          {active.blocks && active.blocks_layout ? (
            <RenderBlocks
              // A página onde o bloco está: sem isto, um bloco de dentro da aba
              // (a Galeria, por exemplo) não sabe o caminho. Mesmo padrão do
              // Grid do core.
              metadata={metadata || properties}
              content={{
                blocks: active.blocks,
                blocks_layout: active.blocks_layout,
              }}
            />
          ) : (
            active.content && (
              <div className="ag-tabs__content">
                {active.content.split(/\n+/).map((para, i) =>
                  para.trim() ? <p key={i}>{para}</p> : null,
                )}
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
};

TabsView.propTypes = {
  data: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    items: PropTypes.array,
    data: PropTypes.object,
  }).isRequired,
  metadata: PropTypes.object,
  properties: PropTypes.object,
};

export default TabsView;
